import { useEffect, useState } from 'react';
import { Row, Col, Spin, Button } from 'antd';
import { getOneState, getReportForSellers, getStatesTree } from '../../../services/adminApi';
import { getListTreeForReports } from '../../../services/categoryApi';
import type { CategoryTreeNode } from '../monthly-test/types';
import { useAuth } from '../../../hooks/auth';
import BranchFilter from './components/BranchFilter';
import TestsFilter from './components/TestsFilter';
import PeriodFilter from './components/PeriodFilter';
import AdditionalOptionsFilter from './components/AdditionalOptionsFilter';
import {
  buildReportFileName,
  downloadBlobXlsx,
} from './helpers/reportHelpers';
import type {
  ReportsProps,
  SellerReportFilters,
  SellerReportRequestFilters,
  StateTreeNode,
  ReportOneState,
} from './reports.types';

const ReportSellers = (_: ReportsProps) => {
  const [states, setStates] = useState<StateTreeNode[]>([]);
  const [oneState, setOneState] = useState<ReportOneState | null>(null);
  const [categoriesWithMaterials, setCategoriesWithMaterials] = useState<CategoryTreeNode[]>([]);
  const [isLoadingReport, setIsLoadingReport] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [filters, setFilters] = useState<SellerReportFilters>({
    state: [],
    period: [],
    content: [],
    tests: [],
  });

  const { user } = useAuth();
  const hasOneStateAccess = user.type === 'registered' && Object.keys(user.extraData.permissions).length > 0;

  useEffect(() => {
    setIsLoading(true);
    if (hasOneStateAccess) {
      getOneState(user.stateId).then(result => {
        setOneState(result);
        setStates([]);
      });
    } else {
      getStatesTree().then(result => {
        setStates(result);
      });
    }

    getListTreeForReports().then(result => {
      setCategoriesWithMaterials(result);
      setIsLoading(false);
    });
  }, [hasOneStateAccess, user.stateId]);

  const isValidReportPeriod = (selectedPeriod: string[]) => selectedPeriod.length === 2 && selectedPeriod.every((date) => Boolean(date));
  const isDownloadDisabled = !(hasOneStateAccess || filters.state.length > 0) || !isValidReportPeriod(filters.period);

  const downloadReport = async () => {
    setIsLoadingReport(true);
    try {
      const reportFilters: SellerReportRequestFilters = {
        state: hasOneStateAccess ? [user.stateId] : filters.state,
        period: filters.period,
        content: filters.content,
        tests: filters.tests.length > 0 ? filters.tests : undefined,
      };
      const result = await getReportForSellers(reportFilters);
      downloadBlobXlsx(result, buildReportFileName(filters.period));
    } catch (error) {
      console.error('Ошибка при скачивании отчета:', error);
    } finally {
      setIsLoadingReport(false);
    }
  };

  return (
    <Spin spinning={isLoading}>
      <Row gutter={[16, 16]} style={{ marginTop: 40 }}>
        <Col span={24} style={{ fontSize: 25 }}>
          Отчет по продавцам
        </Col>
        <Col sm={8} xs={24}>
          <BranchFilter
            hasOneStateAccess={hasOneStateAccess}
            oneState={oneState}
            states={states}
            selectedState={filters.state}
            onChange={(values) => setFilters((prev) => ({ ...prev, state: values }))}
          />
        </Col>
        <Col sm={8} xs={24}>
          <TestsFilter
            label="Материалы"
            placeholder="Выберите материалы"
            categoriesWithMaterials={categoriesWithMaterials}
            selectedValues={filters.tests}
            onChange={(values) => setFilters((prev) => ({ ...prev, tests: values }))}
          />
        </Col>
        <Col sm={8} xs={24}>
          <PeriodFilter onChange={(dateString) => setFilters((prev) => ({ ...prev, period: dateString }))} />
        </Col>
        <Col span={24}>
          <AdditionalOptionsFilter
            selectedContent={filters.content}
            onChange={(value, checked) => {
              setFilters((prev) => {
                const nextContent = checked
                  ? (prev.content.includes(value) ? prev.content : [...prev.content, value])
                  : prev.content.filter((item) => item !== value);

                return { ...prev, content: nextContent };
              });
            }}
          />
        </Col>
        <Col>
          <Button onClick={downloadReport} disabled={isDownloadDisabled} loading={isLoadingReport && !isLoading}>
            {isLoadingReport && !isLoading ? 'Скачивание...' : 'Скачать'}
          </Button>
        </Col>
      </Row>
    </Spin>
  );
};

export default ReportSellers;